import { makeAutoObservable, runInAction } from 'mobx';
import { ProductTemplateSortBy, ProductTemplateSortOrder, ShelfProductInfo } from 'types/productServiceV2';
import { getShelfProductTemplates } from 'api/operations/productServiceV2';
import { createContext, useContext } from 'react';
import { UploadJsonModalDelegate } from './uploadJsonModalDelegate';
import { DeleteTemplateModalDelegate } from './deleteTemplateModalDelegate';

export default class ShelfRFQProductTemplatesStore {
  private _isLoading = false;
  private _shelfProductTemplates: ShelfProductInfo[] = [];

  public uploadJsonModalDelegate = new UploadJsonModalDelegate();
  public deleteTemplateModalDelegate = new DeleteTemplateModalDelegate();

  // Filters / Sorting / Pagination
  private _productLineName: string = '';
  private _searchProductName = '';
  private _page = 1;
  private _totalPages = 1;
  private _totalElements = 0;
  private _sortBy: ProductTemplateSortBy = ProductTemplateSortBy.NAME;
  private _sortOrder: ProductTemplateSortOrder = ProductTemplateSortOrder.ASC;
  private _effectiveYear = new Date().getFullYear().toString();

  // Getters
  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get shelfProductTemplates(): ShelfProductInfo[] {
    return this._shelfProductTemplates;
  }

  public get searchProductName(): string {
    return this._searchProductName;
  }

  public get page(): number {
    return this._page;
  }

  public get totalPages(): number {
    return this._totalPages;
  }

  public get totalElements(): number {
    return this._totalElements;
  }

  public get sortBy(): ProductTemplateSortBy {
    return this._sortBy;
  }

  public get sortOrder(): ProductTemplateSortOrder {
    return this._sortOrder;
  }

  public get productLineName(): string {
    return this._productLineName;
  }

  public get effectiveYear(): string {
    return this._effectiveYear;
  }

  // Setters
  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public setProductLineName(productLineName: string): void {
    this._productLineName = productLineName;
  }

  public setSearchProductName(searchValue: string): void {
    this._searchProductName = searchValue;
  }

  public setPage(page: number): void {
    this._page = page;
  }

  public setSortBy(sortBy: ProductTemplateSortBy): void {
    this._sortBy = sortBy;
  }

  public setSortOrder(sortOrder: ProductTemplateSortOrder): void {
    this._sortOrder = sortOrder;
  }

  public setTotalPages(totalPages: number): void {
    this._totalPages = totalPages;
  }

  public setEffectiveYear(effectiveYear: string): void {
    this._effectiveYear = effectiveYear;
  }

  private setTotalElements(totalElements: number): void {
    this._totalElements = totalElements;
  }

  // API methods
  public async getInitialData(): Promise<void> {
    this.setIsLoading(true);
    await this.getShelfProductTemplates();

    runInAction(() => {
      this.setIsLoading(false);
    });
  }

  public async getShelfProductTemplates(): Promise<void> {
    this.setIsLoading(true);
    const result = await getShelfProductTemplates({
      page: this.page,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      filter: {
        status: null,
        productName: this.searchProductName || null,
        productLine: this._productLineName || null,
        effectiveYear: this._effectiveYear || null,
      },
    });
    runInAction(() => {
      this.setTotalElements(result.totalElements);
      this.setTotalPages(result.totalPages);
      this._shelfProductTemplates = result.result;

      this.setIsLoading(false);
    });
  }

  public resetInitialData(): void {
    this._shelfProductTemplates = [];

    this.setProductLineName('');
    this.setSearchProductName('');
    this.setPage(1);
    this.setTotalPages(1);
    this.setTotalElements(0);
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const PageStoreContext = createContext(new ShelfRFQProductTemplatesStore());

export const useShelfRFQProductTemplatesStore = (): ShelfRFQProductTemplatesStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
